import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import usedBanksActions from 'store/features/used-banks/actions';
import { CreateUsedBankDialog, UpdateUsedBankDialog } from './components';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { selectUsedBanks, selectUsedBanksIsEmpty, selectUsedBanksLoading } from 'store/features/used-banks/selector';

const UsedBanks = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // redux hooks
  const dispatch = useAppDispatch();
  const usedBanks = useAppSelector(selectUsedBanks);
  const loading = useAppSelector(selectUsedBanksLoading);
  const isEmptyUsedBanks = useAppSelector(selectUsedBanksIsEmpty);

  // useState hooks
  const [openDeleteUsedBank, setOpenDeleteUsedBank] = useState(false);
  const [openCreateUsedBank, setOpenCreateUsedBank] = useState(false);
  const [openUpdateUsedBank, setOpenUpdateUsedBank] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'usedBanks.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(usedBanksActions.getUsedBanks());
  };

  // sync helper functions
  const handleCloseDeleteUsedBank = () => setOpenDeleteUsedBank(false);
  const handleClickOpenDeleteUsedBank = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;
    setOpenDeleteUsedBank(true);
  };

  const handleCloseCreateUsedBank = () => setOpenCreateUsedBank(false);
  const handleClickOpenCreateUsedBank = () => setOpenCreateUsedBank(true);

  const handleCloseUpdateUsedBank = () => setOpenUpdateUsedBank(false);
  const handleClickOpenUpdateUsedBank = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;
    setOpenUpdateUsedBank(true);
  };

  // useEffect hooks
  useEffect(() => {
    if (!usedBanks.length && !isEmptyUsedBanks) {
      dispatch(usedBanksActions.getUsedBanks());
    }
  }, [usedBanks, isEmptyUsedBanks, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-bank']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateUsedBank}>
              <FormattedMessage id="usedBanks.createUsedBank" defaultMessage="Banka Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-bank']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateUsedBank}>
              <FormattedMessage id="usedBanks.updateUsedBank" defaultMessage="Banka Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-bank']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteUsedBank}>
              <FormattedMessage id="usedBanks.deleteUsedBank" defaultMessage="Banka Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateUsedBankDialog open={openCreateUsedBank} handleClose={handleCloseCreateUsedBank} refresh={refresh} />
      <UpdateUsedBankDialog
        open={openUpdateUsedBank}
        usedBankId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateUsedBank}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteUsedBank}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteUsedBank}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="usedBanks.deleteUsedBank" defaultMessage="Banka Sil" />}
        subTitle={
          <FormattedMessage
            id="usedBanks.deleteUsedBankRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra banka veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteUsedBank}
        successMessage={
          <FormattedMessage
            id="usedBanks.deleteUsedBankCompleted"
            defaultMessage="Seçili bankayı silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={usedBanks}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default UsedBanks;
